import {
  inject,
  Injectable,
} from '@angular/core';
import {JwtService} from '../../jwt/jwt.service';
import {isArrayContainsAtLeastOneElementFromArray} from '../../../../../common/functions/arrays';
import {
  SmartSubscriptionAccessMode,
  SmartSubscriptionPlan,
} from '../../../../../models/sobrado-smart.model';

@Injectable({
  providedIn: 'root',
})
export class FeatureAclService {

  private jwtService = inject(JwtService);

  /**
   * Method: Check access Benchmarking
   */
  public isBenchmarking(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.benchmarking;
  }

  /**
   * Method: Check access Industry comparison
   */
  public isIndustryComparison(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.benchmarking_industry_comparison;
  }

  /**
   * Method: Check access in gcc for Broker
   */
  isBrokerGCC(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.products_gcc;
  }

  /**
   * Method: Check access in gcc for Insurer
   */
  isInsurerGCC(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.show_gcc_comparison;
  }

  /**
   * Method: Check access for Premium Insurer
   */
  isPremium(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.is_premium;
  }

  /**
   * Method: Check access to tab Factsheet
   */
  isFactsheetEnabled(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.enableFactSheet;
  }

  /**
   * Method: Check access to tab Pension Report
   */
  isPensionReportEnabled(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.enablePensionReport;
  }

  /**
   * Method: Checks if CNG - Call New Generation feature is enabled for the company.
   * @returns {boolean} True if CNG feature is enabled, false otherwise.
   */
  isCngEnabled(): boolean {
    const {cngEnabled} = this.jwtService.decodedToken?.company?.features || {};
    return !!cngEnabled;
  }

  /**
   * Method: Check access on GCC for Broker and Insurer
   */
  gccAccess(): boolean {
    return this.isBrokerGCC() || this.isInsurerGCC();
  }

  /**
   * Method: Check access to interviewOverallExport feature
   */
  hasOverallExportAccess(): boolean {
    return this.jwtService.decodedToken?.company?.features?.interviewOverallExport;
  }

  /**
   * Method: Check access to Inter-Pension functionality for insurers
   */
  hasInterPensionAccess(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.interPensionMember;
  }

  /**
   * Method: Check access to Interview full JSON export functionality
   */
  hasInterviewFullJsonExportAccess(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.interviewFullJsonExport;
  }

  /**
   * Method: Check access to Teams feature
   */
  public hasTeamsAccess(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.teams;
  }

  /**
   * Method: Check company access to Smart Subscription
   */
  private hasCompanySmartSubscription(): SmartSubscriptionPlan {
    return this.jwtService.decodedToken?.company?.features?.smartSubscription as SmartSubscriptionPlan;
  }

  /**
   * Method: Check access to Smart Subscription Mode
   */
  private hasSmartSubscriptionMode(): SmartSubscriptionAccessMode {
    return this.jwtService.decodedToken?.company?.features?.smartSubscriptionMode as SmartSubscriptionAccessMode;
  }

  /**
   * Method: Check account access to Smart Subscription
   */
  private hasAccountSmartSubscription(): SmartSubscriptionPlan {
    return this.jwtService.decodedToken?.account?.smartSubscription as SmartSubscriptionPlan;
  }

  /**
   * Method: Check feature access to Smart Subscription
   */
  public hasSmartSubscription(): boolean {
    const mode: SmartSubscriptionAccessMode = this.hasSmartSubscriptionMode();
    return !!this.hasCompanySmartSubscription()
      && (mode === SmartSubscriptionAccessMode.ALL
        || mode === null
        || (mode === SmartSubscriptionAccessMode.FIXED && !!this.hasAccountSmartSubscription()));
  }

  /**
   * Method: Check access to Call Anonymization feature
   */
  public hasFeatureCallAnonymization(): boolean {
    return !!this.jwtService.decodedToken?.company?.features?.callAnonymization;
  }

  /**
   * Method: Check own company
   * @param insurerId
   */
  isOwnCompany(insurerId: number): boolean {
    return this.jwtService.decodedToken?.account?.company === +insurerId;
  }

  /**
   * Method: Check active Lobs
   * @param id
   */
  isActiveLoB(id: string): boolean {
    return this.jwtService.getActiveLobs().length
      ? !!this.jwtService.getActiveLobs().find(lobId => lobId === id)
      : true;
  }

  /**
   * Method: Check Lob responsible
   * @param ids
   */
  public isLoBResponsible(ids: (number | string)[]): boolean {
    const numberIds = ids.map(item => +item);
    const isProduct = isArrayContainsAtLeastOneElementFromArray(
      this.jwtService.decodedToken?.account?.products,
      numberIds,
    );
    const isResponsibilities = isArrayContainsAtLeastOneElementFromArray(
      this.jwtService.decodedToken?.account?.responsibilities,
      numberIds,
    );
    const isBSSCResponsibilities = isArrayContainsAtLeastOneElementFromArray(
      this.jwtService.decodedToken?.account?.bsscResponsibilities,
      numberIds,
    );
    return isProduct || isResponsibilities || isBSSCResponsibilities;
  }

  /**
   * Method: Check access to Customized Excel Ops feature
   */
  public hasCustomizedExcelOpsAccess(): boolean {
    return !!this.jwtService.decodedToken?.opsToggle?.customizedExcelOps;
  }

  /**
   * Method: Check access to the Customized Excel Permission feature
   */
  private hasCustomizedExcelPermission(): boolean {
    return !!this.jwtService.decodedToken?.companyPermissionToggle?.customizedExcelPermission;
  }

  /**
   * Method: Check full access to Customized Excel Settings (both flags required)
   */
  public hasCustomizedExcelAccess(): boolean {
    return this.hasCustomizedExcelOpsAccess() && this.hasCustomizedExcelPermission();
  }

  /**
   * Method: Check access to Superset Mis insurer ops
   */
  public hasSupersetMisInsurerOps(): boolean {
    return !!this.jwtService.decodedToken?.opsToggle?.supersetMisInsurerOps;
  }

  /**
   * Method: Check Insurer NextGen access (both ops and company permission flags required).
   * Mirrors the PHP IsInsurerNG assertion (insurerNG ops toggle AND insurerNextGen company permission).
   */
  public isInsurerNextGen(): boolean {
    return !!this.jwtService.decodedToken?.opsToggle?.insurerNG
      && !!this.jwtService.decodedToken?.companyPermissionToggle?.insurerNextGen;
  }

  /**
   * Method: Check full access to Superset Mis for a NextGen insurer (both the superset ops
   * toggle and NextGen access required). Mirrors the PHP IsSupersetMis assertion.
   */
  public hasSupersetMisInsurerAccess(): boolean {
    return this.hasSupersetMisInsurerOps() && this.isInsurerNextGen();
  }

  /**
  * Method: Check access to any MIS feature (Benchmarking or Industry Comparison)
  */
  public hasAnyMisFeature(): boolean {
    return this.isBenchmarking() || this.isIndustryComparison();
  }

  /**
   * Method: Check access to Call Templates Ops toggle (Sobrado Admin flag)
   */
  public hasCallTemplatesOpsAccess(): boolean {
    return !!this.jwtService.decodedToken?.opsToggle?.callTemplatesOps;
  }

  /**
   * Method: Check company permission for Call Templates. Also drives, on its own
   * (independent of {@link hasCallTemplatesAccess}), whether a Broker User sees the
   * "Use your Templates" section in the template-selection dialog — see
   * {@link hasCallTemplatesOpsAccess} for the ops-only gate that controls whether the
   * dialog is offered at all.
   */
  public hasCallTemplatesPermission(): boolean {
    return !!this.jwtService.decodedToken?.companyPermissionToggle?.callTemplatesPermission;
  }

  /**
   * Method: Check full Call Templates access for Broker Admin
   * (ops toggle + company permission)
   */
  public hasCallTemplatesAccess(): boolean {
    return this.hasCallTemplatesOpsAccess()
      && this.hasCallTemplatesPermission();
  }

  /**
   * Method: Check the Entrypoint Dashboard ops toggle that switches the insurer entry point from PHP to SNG
   */
  public hasEntrypointDashboardOpsAccess(): boolean {
    return !!this.jwtService.decodedToken?.opsToggle?.entrypointDashboardOps;
  }

}
